import React from 'react';
import { Dashboard, Chat, QuestionAnswer, BarChart, Build, People, Star, Settings, CalendarMonth, Event, AttachFile, Description, Notifications, LocalLibrary, MonetizationOn, Schedule, Newspaper, Announcement, Restaurant, FitnessCenter, Medication, NotificationsActive, Science, Article, ManageAccounts, Telegram, Category, AutoFixHigh, Devices, Assessment, Storage, AccountCircle, AdminPanelSettings, SmartToy, Groups, Extension, NetworkCheck, MonitorHeart, Translate } from '@mui/icons-material';

// Import page components
import { DashboardPage } from './pages/DashboardPage';
import ChatPage from './pages/ChatPage';
import Files from './pages/Files';
import QuestionnairesPage from './pages/QuestionnairesPage';
import QuestionnairePage from './pages/QuestionnairePage';
import MetricsPage from './pages/MetricsPage';
import CalendarPage from './pages/CalendarPage';
import QuizBuilderPage from './pages/QuizBuilderPage';
import QuizBuilderEditorPage from './pages/expert/QuizBuilderEditorPage';
import AdminQuizBuilderPage from './pages/admin/AdminQuizBuilderPage';
import AdminQuizBuilderEditorPage from './pages/admin/AdminQuizBuilderEditorPage';
import ClientsPage from './pages/ClientsPage';
import ClientPage from '@pages/expert/ClientPage.tsx'; // Import ClientPage
import ExpertsPage from './pages/ExpertsPage';
import SettingsPage from '@pages/Settings/SettingsPage.tsx';
import ExpertAppointmentsPage from './pages/expert/AppointmentsPage';
import ClientAppointmentsPage from './pages/client/AppointmentsPage';
import StaticContentPage from './pages/StaticContentPage';
import NotificationsPage from './pages/Notifications';
import { CalculatedMetricsPage } from './pages/CalculatedMetricsPage';
import DevelopmentPage from './pages/DevelopmentPage';
import AiTestPage from './pages/AiTestPage';
import { LibraryPage } from './pages/LibraryPage'; // Import the new LibraryPage
import { AdminFoodsPage } from './pages/AdminFoodsPage';
import { AdminMedicationsPage } from './pages/AdminMedicationsPage';
import { AdminWorkoutsPage } from './pages/AdminWorkoutsPage';
import { PaymentSuccessPage } from './pages/PaymentSuccessPage';
import { AdminFinancePage } from '@pages/admin/AdminFinancePage.tsx';
import { AdminExpertFinancePage } from '@pages/admin/AdminExpertFinancePage.tsx';
import { AdminFinanceClientsPage } from '@pages/admin/AdminFinanceClientsPage.tsx';
import { AdminFinanceExpertsPage } from '@pages/admin/AdminFinanceExpertsPage.tsx';
import { AdminClientEntitlementsPage } from '@pages/admin/AdminClientEntitlementsPage.tsx';
import { AdminExpertEntitlementsPage } from '@pages/admin/AdminExpertEntitlementsPage.tsx';
import { AdminSchedulerPage } from '@pages/admin/AdminSchedulerPage.tsx';
import { AdminNewsPage } from '@pages/admin/AdminNewsPage.tsx';
import { NewsPage } from './pages/NewsPage';
import { AdminNotificationsPage } from '@pages/admin/AdminNotificationsPage.tsx';
import { AdminMetActivitiesPage } from './pages/AdminMetActivitiesPage';
import { AdminLegacyImportPage } from '@pages/admin/AdminLegacyImportPage.tsx';
import { AdminHealthIndexCalculatorPage } from '@pages/admin/AdminHealthIndexCalculatorPage.tsx';
import { AdminEhiJournalRedirect } from '@pages/admin/AdminEhiJournalPage.tsx';
import { AdminClientsPage } from '@pages/admin/AdminClientsPage.tsx';
import { AdminExpertsPage } from '@pages/admin/AdminExpertsPage.tsx';
import { AdminAllUsersPage } from '@pages/admin/AdminAllUsersPage.tsx';
import { PmAllUsersPage } from '@pages/pm/PmAllUsersPage.tsx';
import { PmLegacyClientsPage } from '@pages/pm/legacy/PmLegacyClientsPage.tsx';
import { PmLegacyExpertsPage } from '@pages/pm/legacy/PmLegacyExpertsPage.tsx';
import { PmLegacyOrgsPage } from '@pages/pm/legacy/PmLegacyOrgsPage.tsx';
import { PmClientsPage } from '@pages/pm/PmClientsPage.tsx';
import { PmExpertsPage } from '@pages/pm/PmExpertsPage.tsx';
import { PmSourcesPage } from '@pages/pm/PmSourcesPage.tsx';
import { PmClientPage } from '@pages/pm/PmClientPage.tsx';
import { PmExpertPage } from '@pages/pm/PmExpertPage.tsx';
import { AdminDevicesPage } from '@pages/admin/AdminDevicesPage.tsx';
import { ExpertPostsPage } from './pages/expert/ExpertPostsPage';
import { ExpertPromptsPage } from './pages/expert/ExpertPromptsPage';
import { DocumentationPage } from './pages/pm/DocumentationPage';
import ClientExpertPostsPage from './pages/client/ExpertPostsPage';
import { AdminTelegramPage } from '@pages/admin/AdminTelegramPage.tsx';
import { AdminEventTypesPage } from '@pages/admin/AdminEventTypesPage.tsx';
import ExpertPage from './pages/client/ExpertPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import SurveyInvitePage from './pages/client/SurveyInvitePage';
import DevPushPage from './pages/DevPushPage';
import { MyReportsPage } from './pages/client/MyReportsPage';
import { AdminAiFoodCachePage } from '@pages/admin/AdminAiFoodCachePage.tsx';
import { AdminAiExtractCachePage } from '@pages/admin/AdminAiExtractCachePage.tsx';
import { AdminAiReportsPage } from '@pages/admin/AdminAiReportsPage.tsx';
import { AdminAiWorkoutCachePage } from '@pages/admin/AdminAiWorkoutCachePage.tsx';
import { DevAiIndexPage } from './pages/DevAiIndexPage';
import { DevMissingTranslationsPage } from './pages/DevMissingTranslationsPage';
import { DevI18nGapsPage } from './pages/DevI18nGapsPage';
import { DevNativeIapPage } from './pages/DevNativeIapPage';
import { DevPaymentDiagnosticsPage } from './pages/DevPaymentDiagnosticsPage';
import { DevFinanceLegacyPage } from './pages/DevFinanceLegacyPage';
import AdminSystemSettingsPage from '@pages/admin/AdminSystemSettingsPage.tsx';
import { AdminSystemPromptsPage } from '@pages/admin/AdminSystemPromptsPage.tsx';

import { ProfilePage } from './pages/ProfilePage';

// Define a type for our route configuration
export interface RouteConfig {
  path: string;
  component: React.ComponentType;
  name?: string; // i18n key for the sidebar/topbar label
  icon?: React.ReactElement; // Icon for the sidebar
  allowedRoles?: string[]; // Roles that can access this route
  showInSidebar: boolean; // Whether to show in the sidebar
  isPublic?: boolean; // If true, accessible without authentication
  group?: string; // i18n key for group name in the sidebar
  requiredPlan?: 'pro'; // Show a PRO badge in the sidebar
  requiredClientFeature?: string; // Client-role only (Sidebar.tsx): hide entirely (not badge) unless useClientPlan().hasFeature() is true
}

// Central route configuration
export const routeConfig: RouteConfig[] = [
  // Sidebar items
  {
    path: '/dashboard',
    component: DashboardPage,
    name: 'routes.dashboard',
    icon: <Dashboard />,
    showInSidebar: true,
  },
  {
    path: '/profile',
    component: ProfilePage,
    name: 'routes.profile',
    icon: <AccountCircle />,
    showInSidebar: false,
  },
  {
    path: '/news',
    component: NewsPage,
    name: 'routes.news',
    icon: <Announcement />,
    showInSidebar: false,
  },
  {
    path: '/clients',
    component: ClientsPage,
    name: 'routes.clients',
    icon: <People />,
    allowedRoles: ['expert'],
    showInSidebar: true,
  },
  {
    path: '/chat',
    component: ChatPage,
    name: 'routes.chat',
    icon: <Chat />,
    allowedRoles: ['expert', 'client'],
    showInSidebar: true,
    requiredClientFeature: 'chat',
  },
  {
    path: '/files',
    component: Files,
    name: 'routes.files',
    icon: <AttachFile />,
    allowedRoles: [],
    showInSidebar: true,
  },
  {
    path: '/questionnaires',
    component: QuestionnairesPage,
    name: 'routes.questionnaires',
    icon: <QuestionAnswer />,
    allowedRoles: ['client'],
    showInSidebar: true,
  },
  {
    path: '/metrics',
    component: MetricsPage,
    name: 'routes.metrics',
    icon: <BarChart />,
    allowedRoles: ['client'],
    showInSidebar: true,
  },
  {
    path: '/calculated-metrics',
    component: CalculatedMetricsPage,
    name: 'routes.calculatedMetrics',
    icon: <BarChart />,
    allowedRoles: ['expert'],
    showInSidebar: true,
  },
  {
    path: '/calendar',
    component: CalendarPage,
    name: 'routes.calendar',
    icon: <CalendarMonth />,
    allowedRoles: ['client', 'expert'],
    showInSidebar: true,
  },
  {
    path: '/expert/appointments',
    component: ExpertAppointmentsPage,
    name: 'routes.appointments',
    icon: <Event />,
    allowedRoles: ['expert'],
    showInSidebar: true,
  },
  {
    path: '/appointments',
    component: ClientAppointmentsPage,
    name: 'routes.appointments',
    icon: <Event />,
    allowedRoles: ['client'],
    showInSidebar: true,
    requiredClientFeature: 'appointments',
  },
  {
    path: '/quiz-builder',
    component: QuizBuilderPage,
    name: 'routes.quizBuilder',
    icon: <Build />,
    allowedRoles: ['expert'],
    showInSidebar: true,
  },
  {
    path: '/admin/finance',
    component: AdminFinancePage,
    name: 'routes.adminFinance',
    icon: <MonetizationOn />,
    allowedRoles: ['developer'], // TEMP: тимчасово прибрано superadmin, поки старий стовпчик 'Тариф' на цих сторінках заблокований — див. workspace/current-work/standard-pro-feature-abstraction.md
    showInSidebar: true,
    group: 'sidebar.group.finance',
  },
  {
    path: '/admin/expert-finance',
    component: AdminExpertFinancePage,
    name: 'routes.adminExpertFinance',
    icon: <MonetizationOn />,
    allowedRoles: ['developer'], // TEMP: тимчасово прибрано superadmin, поки старий стовпчик 'Тариф' на цих сторінках заблокований — див. workspace/current-work/standard-pro-feature-abstraction.md
    showInSidebar: true,
    group: 'sidebar.group.finance',
  },
  {
    path: '/admin/finance/clients',
    component: AdminFinanceClientsPage,
    name: 'routes.adminFinanceClients',
    icon: <People />,
    allowedRoles: ['developer'], // TEMP: тимчасово прибрано superadmin, поки старий стовпчик 'Тариф' на цих сторінках заблокований — див. workspace/current-work/standard-pro-feature-abstraction.md
    showInSidebar: true,
    group: 'sidebar.group.finance',
  },
  {
    path: '/admin/finance/experts',
    component: AdminFinanceExpertsPage,
    name: 'routes.adminFinanceExperts',
    icon: <Groups />,
    allowedRoles: ['developer'], // TEMP: тимчасово прибрано superadmin, поки старий стовпчик 'Тариф' на цих сторінках заблокований — див. workspace/current-work/standard-pro-feature-abstraction.md
    showInSidebar: true,
    group: 'sidebar.group.finance',
  },
  {
    path: '/admin/entitlements/clients',
    component: AdminClientEntitlementsPage,
    name: 'routes.adminClientEntitlements',
    icon: <Extension />,
    allowedRoles: ['superadmin', 'developer'],
    showInSidebar: true,
    group: 'sidebar.group.entitlements',
  },
  {
    path: '/admin/entitlements/experts',
    component: AdminExpertEntitlementsPage,
    name: 'routes.adminExpertEntitlements',
    icon: <Groups />,
    allowedRoles: ['superadmin', 'developer'],
    showInSidebar: true,
    group: 'sidebar.group.entitlements',
  },
  {
    path: '/admin/quiz-builder',
    component: AdminQuizBuilderPage,
    name: 'routes.adminQuizBuilder',
    icon: <Build />,
    allowedRoles: ['admin', 'superadmin'],
    showInSidebar: true,
    group: 'sidebar.group.library',
  },
  {
    path: '/expert/posts',
    component: ExpertPostsPage,
    name: 'routes.expertPosts',
    icon: <Article />,
    allowedRoles: ['expert'],
    showInSidebar: true,
    requiredPlan: 'pro',
  },
  {
    path: '/expert/prompts',
    component: ExpertPromptsPage,
    name: 'routes.expertPrompts',
    icon: <AutoFixHigh />,
    allowedRoles: ['expert'],
    showInSidebar: true,
  },
  {
    path: '/experts',
    component: ExpertsPage,
    name: 'routes.experts',
    icon: <Star />,
    allowedRoles: ['client'],
    showInSidebar: true,
    requiredClientFeature: 'expert_connections',
  },
  {
    path: '/expert-posts',
    component: ClientExpertPostsPage,
    name: 'routes.expertPostsClient',
    icon: <Article />,
    allowedRoles: ['client'],
    showInSidebar: true,
    requiredClientFeature: 'expert_posts',
  },
  {
    path: '/reports',
    component: MyReportsPage,
    name: 'routes.reports',
    icon: <Assessment />,
    allowedRoles: ['client'],
    showInSidebar: true,
  },
  {
    path: '/library',
    component: LibraryPage,
    name: 'routes.library',
    icon: <LocalLibrary />,
    allowedRoles: ['expert'],
    showInSidebar: true,
  },
  {
    path: '/admin/users/clients',
    component: AdminClientsPage,
    name: 'routes.adminClients',
    icon: <ManageAccounts />,
    allowedRoles: ['superadmin', 'admin'],
    showInSidebar: true,
    group: 'sidebar.group.users',
  },
  {
    path: '/admin/users/experts',
    component: AdminExpertsPage,
    name: 'routes.adminExperts',
    icon: <ManageAccounts />,
    allowedRoles: ['superadmin', 'admin'],
    showInSidebar: true,
    group: 'sidebar.group.users',
  },
  {
    path: '/admin/users/all',
    component: AdminAllUsersPage,
    name: 'routes.adminAllUsers',
    icon: <Groups />,
    allowedRoles: ['superadmin', 'admin'],
    showInSidebar: true,
    group: 'sidebar.group.users',
  },
  {
    path: '/pm/users/clients',
    component: PmClientsPage,
    name: 'routes.pmClients',
    icon: <ManageAccounts />,
    allowedRoles: ['pm'],
    showInSidebar: true,
    group: 'sidebar.group.users',
  },
  {
    path: '/pm/client/:id/*',
    component: PmClientPage,
    allowedRoles: ['pm'],
    showInSidebar: false,
  },
  {
    path: '/pm/expert/:id/*',
    component: PmExpertPage,
    allowedRoles: ['pm'],
    showInSidebar: false,
  },
  {
    path: '/pm/users/experts',
    component: PmExpertsPage,
    name: 'routes.pmExperts',
    icon: <ManageAccounts />,
    allowedRoles: ['pm'],
    showInSidebar: true,
    group: 'sidebar.group.users',
  },
  {
    path: '/pm/sources',
    component: PmSourcesPage,
    name: 'routes.pmSources',
    icon: <LocalLibrary />,
    allowedRoles: ['pm'],
    showInSidebar: true,
    group: 'sidebar.group.library',
  },
  {
    path: '/pm/users/all',
    component: PmAllUsersPage,
    name: 'routes.pmAllUsers',
    icon: <Groups />,
    allowedRoles: ['pm'],
    showInSidebar: true,
    group: 'sidebar.group.users',
  },
  {
    path: '/pm/legacy/clients',
    component: PmLegacyClientsPage,
    name: 'routes.pmLegacyClients',
    icon: <ManageAccounts />,
    allowedRoles: ['pm'],
    showInSidebar: true,
    group: 'sidebar.group.legacy',
  },
  {
    path: '/pm/legacy/experts',
    component: PmLegacyExpertsPage,
    name: 'routes.pmLegacyExperts',
    icon: <ManageAccounts />,
    allowedRoles: ['pm'],
    showInSidebar: true,
    group: 'sidebar.group.legacy',
  },
  {
    path: '/pm/legacy/orgs',
    component: PmLegacyOrgsPage,
    name: 'routes.pmLegacyOrgs',
    icon: <Groups />,
    allowedRoles: ['pm'],
    showInSidebar: true,
    group: 'sidebar.group.legacy',
  },
  {
    path: '/admin/devices',
    component: AdminDevicesPage,
    name: 'routes.adminDevices',
    icon: <Devices />,
    allowedRoles: ['superadmin', 'admin'],
    showInSidebar: true,
    group: 'sidebar.group.users',
  },
  {
    path: '/admin/foods',
    component: AdminFoodsPage,
    name: 'routes.adminFoods',
    icon: <Restaurant />,
    allowedRoles: ['superadmin', 'pm'],
    showInSidebar: true,
    group: 'sidebar.group.library',
  },
  {
    path: '/admin/medications',
    component: AdminMedicationsPage,
    name: 'routes.adminMedications',
    icon: <Medication />,
    allowedRoles: ['superadmin', 'pm'],
    showInSidebar: true,
    group: 'sidebar.group.library',
  },
  {
    path: '/admin/workouts',
    component: AdminWorkoutsPage,
    name: 'routes.adminWorkouts',
    icon: <FitnessCenter />,
    allowedRoles: ['superadmin', 'pm'],
    showInSidebar: true,
    group: 'sidebar.group.library',
  },
  {
    path: '/admin/met-activities',
    component: AdminMetActivitiesPage,
    name: 'routes.adminMetActivities',
    icon: <Science />,
    allowedRoles: ['superadmin', 'pm'],
    showInSidebar: true,
    group: 'sidebar.group.library',
  },
  {
    path: '/admin/event-types',
    component: AdminEventTypesPage,
    name: 'routes.adminEventTypes',
    icon: <Category />,
    allowedRoles: ['pm'],
    showInSidebar: true,
    group: 'sidebar.group.library',
  },
  {
    path: '/admin/extract_cache',
    component: AdminAiExtractCachePage,
    name: 'routes.adminExtractCache',
    icon: <Article />,
    allowedRoles: ['superadmin', 'admin', 'pm'],
    showInSidebar: true,
    group: 'sidebar.group.aiCache',
  },
  {
    path: '/admin/ai_reports',
    component: AdminAiReportsPage,
    name: 'routes.adminAiReports',
    icon: <Assessment />,
    allowedRoles: ['superadmin', 'admin', 'pm'],
    showInSidebar: true,
    group: 'sidebar.group.aiCache',
  },
  {
    // Системні промпти (2026-09-30): окрема сторінка (раніше - вкладка системних налаштувань). Змінюють лише
    // developer / superadmin, admin - перегляд (перевіряє й бекенд).
    path: '/admin/system-prompts',
    component: AdminSystemPromptsPage,
    name: 'routes.adminSystemPrompts',
    icon: <SmartToy />,
    allowedRoles: ['superadmin', 'admin', 'pm'],
    showInSidebar: true,
    group: 'sidebar.group.aiCache',
  },
  {
    path: '/admin/foods_cache',
    component: AdminAiFoodCachePage,
    name: 'routes.adminFoodsCache',
    icon: <Restaurant />,
    allowedRoles: ['superadmin', 'admin', 'pm'],
    showInSidebar: true,
    group: 'sidebar.group.aiCache',
  },
  {
    path: '/admin/workouts_cache',
    component: AdminAiWorkoutCachePage,
    name: 'routes.adminWorkoutsCache',
    icon: <FitnessCenter />,
    allowedRoles: ['superadmin', 'admin', 'pm'],
    showInSidebar: true,
    group: 'sidebar.group.aiCache',
  },
  {
    path: '/admin/system-settings',
    component: AdminSystemSettingsPage,
    name: 'routes.adminSystemSettings',
    icon: <AdminPanelSettings />,
    allowedRoles: ['superadmin', 'developer', 'admin'],
    showInSidebar: true,
    group: 'sidebar.group.other',
  },
  {
    path: '/admin/scheduler',
    component: AdminSchedulerPage,
    name: 'routes.adminScheduler',
    icon: <Schedule />,
    allowedRoles: ['superadmin', 'developer'],
    showInSidebar: true,
    group: 'sidebar.group.other',
  },
  {
    path: '/admin/news',
    component: AdminNewsPage,
    name: 'routes.adminNews',
    icon: <Newspaper />,
    allowedRoles: ['superadmin', 'admin'],
    showInSidebar: true,
    group: 'sidebar.group.other',
  },
  {
    path: '/admin/notifications',
    component: AdminNotificationsPage,
    name: 'routes.adminNotifications',
    icon: <NotificationsActive />,
    allowedRoles: ['superadmin', 'admin'],
    showInSidebar: true,
    group: 'sidebar.group.other',
  },
  {
    path: '/admin/telegram',
    component: AdminTelegramPage,
    name: 'routes.adminTelegram',
    icon: <Telegram />,
    allowedRoles: ['superadmin', 'admin'],
    showInSidebar: true,
    group: 'sidebar.group.other',
  },
  {
    path: '/admin/legacy-import',
    component: AdminLegacyImportPage,
    name: 'routes.adminLegacyImport',
    icon: <Schedule />,
    allowedRoles: ['superadmin', 'admin'],
    showInSidebar: true,
    group: 'sidebar.group.other',
  },
  {
    path: '/admin/health-index',
    component: AdminHealthIndexCalculatorPage,
    name: 'routes.adminHealthIndex',
    icon: <MonitorHeart />,
    allowedRoles: ['superadmin', 'developer', 'admin'],
    showInSidebar: true,
    group: 'sidebar.group.other',
  },
  {
    // Той самий калькулятор у зоні продакта: окремий шлях, щоб пункт був у сайдбарі режиму "pm".
    path: '/pm/health-index',
    component: AdminHealthIndexCalculatorPage,
    name: 'routes.adminHealthIndex',
    icon: <MonitorHeart />,
    allowedRoles: ['pm'],
    showInSidebar: true,
  },
  {
    // Журнал EHI злито з /admin/health-index (2026-09-22) - тут лише редірект для старих посилань.
    path: '/admin/ehi-journal',
    component: AdminEhiJournalRedirect,
    name: 'routes.adminEhiJournal',
    icon: <MonitorHeart />,
    allowedRoles: ['superadmin', 'developer', 'admin'],
    showInSidebar: false,
    group: 'sidebar.group.other',
  },
  {
    path: '/settings',
    component: SettingsPage,
    name: 'routes.settings',
    icon: <Settings />,
    showInSidebar: true,
  },
  {
    path: '/static-content',
    component: StaticContentPage,
    name: 'routes.staticContent',
    icon: <Description />,
    showInSidebar: false,
  },
  {
    path: '/notifications',
    component: NotificationsPage,
    name: 'routes.notifications',
    icon: <Notifications />,
    showInSidebar: true,
  },
  {
    path: '/development',
    component: DevelopmentPage,
    name: 'routes.development',
    icon: <Build />,
    showInSidebar: true,
    allowedRoles: ['developer'],
  },
  {
    path: '/ai-test',
    component: AiTestPage,
    name: 'routes.aiTest',
    icon: <AutoFixHigh />,
    showInSidebar: true,
    allowedRoles: ['developer'],
  },
  {
    path: '/dev-push',
    component: DevPushPage,
    name: 'routes.devPush',
    icon: <NotificationsActive />,
    showInSidebar: true,
    allowedRoles: ['developer'],
  },
  {
    path: '/dev-ai-index',
    component: DevAiIndexPage,
    name: 'routes.devAiIndex',
    icon: <Storage />,
    showInSidebar: true,
    allowedRoles: ['developer'],
  },
  {
    path: '/dev-missing-translations',
    component: DevMissingTranslationsPage,
    name: 'routes.devMissingTranslations',
    icon: <Translate />,
    showInSidebar: true,
    allowedRoles: ['developer'],
  },
  {
    path: '/dev-i18n-gaps',
    component: DevI18nGapsPage,
    name: 'routes.devI18nGaps',
    icon: <Translate />,
    showInSidebar: true,
    allowedRoles: ['developer'],
  },
  {
    path: '/dev-native-iap',
    component: DevNativeIapPage,
    name: 'routes.devNativeIap',
    icon: <MonetizationOn />,
    showInSidebar: true,
    allowedRoles: ['developer'],
    group: 'sidebar.group.finance',
  },
  {
    path: '/dev-payment-diagnostics',
    component: DevPaymentDiagnosticsPage,
    name: 'routes.devPaymentDiagnostics',
    icon: <NetworkCheck />,
    showInSidebar: true,
    allowedRoles: ['developer'],
    group: 'sidebar.group.finance',
  },
  {
    path: '/dev-finance-legacy',
    component: DevFinanceLegacyPage,
    name: 'routes.devFinanceLegacy',
    icon: <Storage />,
    showInSidebar: true,
    // Бекенд - @Roles(OWNER).
    allowedRoles: ['owner'],
  },
  {
    path: '/pm/documentation',
    component: DocumentationPage,
    name: 'routes.documentation',
    icon: <Description />,
    allowedRoles: ['pm'],
    showInSidebar: true,
  },
  // Non-sidebar items
  {
    path: '/chat/:id',
    component: ChatPage,
    allowedRoles: ['admin', 'expert', 'client'],
    showInSidebar: false,
  },
  {
    path: '/questionnaires/:id',
    component: QuestionnairePage,
    allowedRoles: ['admin', 'expert', 'client'],
    showInSidebar: false,
  },
  {
    path: '/quiz-builder/:templateId',
    component: QuizBuilderEditorPage,
    allowedRoles: ['expert'],
    showInSidebar: false,
  },
  {
    path: '/admin/quiz-builder/:templateId',
    component: AdminQuizBuilderEditorPage,
    allowedRoles: ['admin', 'superadmin'],
    showInSidebar: false,
  },
  {
    path: '/client/:id/*',
    component: ClientPage,
    allowedRoles: ['expert', 'admin', 'superadmin'],
    showInSidebar: false,
  },
  {
    path: '/payment/success',
    component: PaymentSuccessPage,
    allowedRoles: ['client'],
    showInSidebar: false,
  },
  {
    path: '/experts/:id',
    component: ExpertPage,
    allowedRoles: ['client'],
    showInSidebar: false,
  },
  {
    path: '/survey-invite/:token',
    component: SurveyInvitePage,
    allowedRoles: ['client'],
    showInSidebar: false,
  },
  {
    path: '/accept-invite',
    component: AcceptInvitePage,
    showInSidebar: false,
    isPublic: true,
  },
];
